import React, { useState, useEffect } from 'react';
import { X, Link as LinkIcon, Eye, Lock, Trash2, Copy, Check, Loader2, Plus } from 'lucide-react';
import {
    createShare, listMyShares, deleteShare
} from '../services/shareService.js';

const ShareModal = ({ isOpen, onClose, currentUser, resumeData, selectedTemplate, showNotification }) => {
    const [shares, setShares] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [usePassword, setUsePassword] = useState(false);
    const [password, setPassword] = useState('');
    const [copiedId, setCopiedId] = useState(null);

    const refresh = async () => {
        if (!currentUser) return;
        setLoading(true);
        try {
            setShares(await listMyShares(currentUser.uid));
        } catch (e) {
            console.error('listMyShares failed:', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (isOpen) refresh(); }, [isOpen, currentUser]); // eslint-disable-line

    const handleCreate = async () => {
        if (!currentUser) return;
        setCreating(true);
        try {
            const id = await createShare(currentUser.uid, resumeData, selectedTemplate, {
                password: usePassword ? password : null
            });
            const url = `${window.location.origin}/?share=${id}`;
            await navigator.clipboard.writeText(url).catch(() => { });
            showNotification(`Share link created and copied to clipboard!`);
            setPassword('');
            setUsePassword(false);
            refresh();
        } catch (e) {
            showNotification(`Share creation failed: ${e.message}`);
        } finally {
            setCreating(false);
        }
    };

    const handleCopy = async (id) => {
        const url = `${window.location.origin}/?share=${id}`;
        try {
            await navigator.clipboard.writeText(url);
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 1500);
        } catch {
            showNotification('Copy failed.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Revoke this share link? The URL will stop working.')) return;
        try {
            await deleteShare(id);
            setShares(prev => prev.filter(s => s.id !== id));
            showNotification('Share link revoked.');
        } catch (e) {
            showNotification(`Delete failed: ${e.message}`);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                    <h3 className="font-bold text-slate-800 flex items-center gap-2">
                        <LinkIcon size={18} className="text-blue-600" />
                        Share Links
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 flex-1 overflow-y-auto">
                    {/* Create new share */}
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4">
                        <p className="text-sm font-bold text-blue-900 mb-3">Create a new share link</p>
                        <label className="flex items-center gap-2 text-sm text-blue-800 mb-3">
                            <input
                                type="checkbox"
                                checked={usePassword}
                                onChange={(e) => setUsePassword(e.target.checked)}
                            />
                            <Lock size={14} /> Protect with password
                        </label>
                        {usePassword && (
                            <input
                                type="text"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Password"
                                className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm mb-3"
                            />
                        )}
                        <button
                            onClick={handleCreate}
                            disabled={creating || (usePassword && !password)}
                            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-2 rounded-lg flex items-center justify-center gap-2"
                        >
                            {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                            Create & Copy Link
                        </button>
                        <p className="text-xs text-blue-700 mt-2">
                            The link is a snapshot of your current resume. Edits won't appear retroactively.
                        </p>
                    </div>

                    {/* Existing shares */}
                    <p className="text-xs font-bold text-slate-400 uppercase mb-2">Your share links</p>
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="animate-spin text-slate-400" size={24} />
                        </div>
                    ) : shares.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-8">No share links yet.</p>
                    ) : (
                        <ul className="space-y-2">
                            {shares.map(s => (
                                <li key={s.id} className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 transition-colors">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="font-medium text-sm text-slate-800 flex items-center gap-2">
                                            {s.title || 'Untitled'}
                                            {s.hasPassword && <Lock size={12} className="text-amber-500" />}
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-slate-500">
                                            <span className="flex items-center gap-1">
                                                <Eye size={12} /> {s.viewCount}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <code className="flex-1 text-xs bg-slate-100 px-2 py-1 rounded truncate">
                                            {window.location.origin}/?share={s.id}
                                        </code>
                                        <button
                                            onClick={() => handleCopy(s.id)}
                                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                            title="Copy URL"
                                        >
                                            {copiedId === s.id ? <Check size={14} /> : <Copy size={14} />}
                                        </button>
                                        <button
                                            onClick={() => handleDelete(s.id)}
                                            className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                                            title="Revoke"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                    {s.lastViewedAt && (
                                        <p className="text-[10px] text-slate-400 mt-1">
                                            Last viewed {s.lastViewedAt.toLocaleString()}
                                        </p>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ShareModal;
